import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { news } from '@/mocks/news';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function NewsDetailPage() {
  const { newsId = '' } = useParams();
  const article = news.find((item) => item.id === newsId);

  usePageMeta({
    title: article ? `${article.title} - 影视资讯 - 河马AV` : '影视资讯 - 河马AV',
    description: article ? article.summary : '河马AV影视资讯详情页。',
    keywords: article ? `${article.topic},影视资讯,河马AV` : '河马AV,影视资讯',
  });

  if (!article) {
    return (
      <Layout>
        <div className="mx-auto flex max-w-3xl flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-3xl text-secondary-600">
            <i className="ri-article-line" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">未找到该篇资讯</h1>
          <p className="mt-3 text-sm text-foreground-500">该文章可能已被移除，您可以返回资讯列表浏览其他内容。</p>
          <Link
            to="/news"
            className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" />
            返回资讯列表
          </Link>
        </div>
      </Layout>
    );
  }

  const paragraphs = article.content.split('\n').filter((p) => p.trim().length > 0);
  const related = news
    .filter((item) => item.id !== article.id && item.topic === article.topic)
    .concat(news.filter((item) => item.id !== article.id && item.topic !== article.topic))
    .slice(0, 3);

  return (
    <Layout>
      <article className="mx-auto max-w-3xl px-4 py-8 md:px-6 md:py-12">
        <Breadcrumb items={[{ label: '资讯动态', to: '/news' }, { label: article.topic }]} />

        <header className="mt-5">
          <span className="rounded bg-accent-100 px-2.5 py-1 text-xs text-accent-800">{article.topic}</span>
          <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl">
            {article.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" />
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {article.date}
            </span>
          </div>
        </header>

        <div className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-background-200">
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={article.title}
            className="aspect-[16/9] w-full object-cover object-top"
          />
        </div>

        <div className="mt-8 flex flex-col gap-5">
          {paragraphs.map((paragraph, index) => (
            <p key={index} className="text-[15px] leading-loose text-foreground-700">
              {paragraph}
            </p>
          ))}
        </div>

        <div className="mt-10 flex items-center justify-between border-t border-background-200 pt-6">
          <Link
            to="/news"
            className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-arrow-left-line" />
            返回资讯列表
          </Link>
          <span className="text-xs text-foreground-400">内容为演示示例</span>
        </div>

        {/* Related */}
        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-accent-500" />
            相关文章推荐
          </h2>
          <div className="mt-5 flex flex-col gap-3">
            {related.map((item) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                className="group flex flex-col gap-1 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200"
              >
                <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                  <span className="text-accent-700">{item.topic}</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line" />
                    {item.date}
                  </span>
                </div>
                <h3 className="line-clamp-2 font-heading text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
                  {item.title}
                </h3>
              </Link>
            ))}
          </div>
        </section>
      </article>
    </Layout>
  );
}